import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../../ui/card";
import { Button } from "../../../ui/button";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Label } from "../../../ui/label";
import {
  Plane,
  MapPin,
  ArrowLeft,
  BedDouble,
  ArrowRight,
  Plus,
  Trash2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../../ui/select";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Switch } from "../../../ui/switch";
import { toast } from "sonner";
import {
  BufferConfig,
  getBufferConfigurations,
  getVenues,
  VenueApi,
  PortApi,
  getPorts,
  getCutoffConfigurations,
  CutoffConfig,
  getPortByPublicId,
  getTourOperatorWithDocuments,
  IntercityCheckoutApi,
  getIntercityCheckouts,
  InstantAvailabilityVenue,
  getInstantBookingAvailability,
  PropertyApi,
  getProperties,
} from "services/tourService";
import { mapTypeFromApi } from "utils/portTypeMapper";
import { CustomDayPicker } from "ui/CustomDayPicker";
import { createTour, getGeneralMasterByCode } from "services/tourService";
import { useAuth } from "contexts/AuthContext";
import { usePageHeader } from "contexts/PageHeaderContext";
import {
  SwitchableTourCategory,
  TourTypeSwitcher,
} from "../components/TourTypeSwitcher";
import {
  calculateVenueSchedule,
  getIntercityCheckout,
  getTotalVenueBuffer,
  getTravelTimeMinutes,
  normalizeTime,
  parseTimeToMinutes,
  canAccommodateAnotherVenue,
} from "utils/venueScheduleCalculator";
import { FormData, RoomAvailability } from "types/instantBookingTypes";
import { LawazimConfig } from "types/lawazimConfig";
import { getLawazimList } from "services/lawazimConfigService";
import AvailableRooms from "./AvailableRooms";
import Confirmation from "./Confirmation";
import { FullScreenLoading } from "components/ui/loader";

interface InstantBookingTourPageProps {
  onNavigate?: (page: string, tourId?: string) => void;
}

enum TourType {
  GENERAL = 1,
  MIQAAT = 2,
}

export default function InstantBookingTourPage({
  onNavigate,
}: InstantBookingTourPageProps) {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [, setCompletedSteps] = useState<number[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [ports, setPorts] = useState<PortApi[]>([]);
  const [venuesFromApi, setVenuesFromApi] = useState<VenueApi[]>([]);
  const [buffers, setBuffers] = useState<BufferConfig[]>([]);
  const [cutoffs, setCutoffs] = useState<CutoffConfig[]>([]);
  const [cutoffDates, setCutoffDates] = useState<string[]>([]);
  const [, setManualDateEdit] = useState(false);
  const [properties, setProperties] = useState<PropertyApi[]>([]);
  const [intercityCheckouts, setIntercityCheckouts] = useState<
    IntercityCheckoutApi[]
  >([]);
  const [portBlocks, setPortBlocks] = useState<Record<number, Date[]>>({});
  const [transportMode, setTransportMode] = useState<"flight" | "road">(
    "flight",
  );
  const [arrivalDate] = useState("");
  const [departureDate] = useState("");
  const [isVenueManuallyChanged, setIsVenueManuallyChanged] = useState(false);
  const [roomAvailability, setRoomAvailability] = useState<RoomAvailability[]>(
    [],
  );
  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(
    new Set(),
  );
  const [expandedRoomDetails, setExpandedRoomDetails] = useState<string | null>(
    null,
  );
  const [operatorExpiryDate, setOperatorExpiryDate] = useState<Date | null>(
    null,
  );
  const [, setTermsAndConditions] = useState("");
  const [loading, setLoading] = useState(false);
  const portBlockedDates = Object.values(portBlocks).flat();

  const blockedDateReasons = Object.entries(portBlocks).flatMap(
    ([portId, dates]) => {
      const port = ports.find((p) => p.portId === Number(portId));

      return dates.map((date) => ({
        date,
        label: `${port?.portName || "Port"} block`,
      }));
    },
  );

  const cutoffBlockedDates = cutoffDates.map((d) => {
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day);
  });

  const tourOperatorId = user?.tourOperatorId ?? null;
  const location = useLocation();
  const selectedMiqaat = location.state?.selectedMiqaat;
  const [availabilityData, setAvailabilityData] = useState<
    InstantAvailabilityVenue[]
  >([]);
  const [lawazimConfigs, setLawazimConfigs] = useState<LawazimConfig[]>([]);
  const [tourOperatorTypeId, setTourOperatorTypeId] = useState<number | null>(
    null,
  );

  const [arrivalCalendarMonth, setArrivalCalendarMonth] = useState<
    Date | undefined
  >(undefined);
  const [departureCalendarMonth, setDepartureCalendarMonth] = useState<
    Date | undefined
  >(undefined);
  const [venueCalendarMonths, setVenueCalendarMonths] = useState<
    Record<string, Date | undefined>
  >({});

  const handleTourTypeChange = (category: SwitchableTourCategory) => {
    if (category === 1) return;
    navigate("/tours/tour-create", {
      state: {
        selectedMiqaat: { type: "general" },
        flowType: category === 3 ? "request" : "external",
        tourCategory: category,
      },
    });
  };

  useEffect(() => {
    setPageHeader({
      title: "Create Instant Booking Tour",
      subtitle: "Quick booking with real-time room availability",
      icon: <BedDouble className="h-6 w-6" />,
      onBack: () => navigate("/tours/create-dashboard"),
    });
    return () => setPageHeader(null);
  }, [navigate, setPageHeader]);

  const [formData, setFormData] = useState<FormData>({
    tourType: "",
    arrivalTransportMode: "flight",
    arrivalAirport: 0,
    arrivalDate: "",
    arrivalTime: "",
    arrivalFlightNumber: "",
    arrivalBookingRef: "",
    arrivalAirline: "",
    arrivalConfirmed: false,
    departureTransportMode: "flight",
    departureAirport: 0,
    departureDate: "",
    departureTime: "",
    departureFlightNumber: "",
    departureBookingRef: "",
    departureAirline: "",
    departureConfirmed: false,
    tourName: "",
    parentTourId: "",
    noOfApplicants: "",
    faizAccommodation: "yes",
    termsAndConditions: "",
    notes: "",
    venues: [
      {
        id: "venue-1",
        venueId: null,
        fromDate: "",
        fromTime: "",
        toDate: "",
        toTime: "",
      },
      {
        id: "venue-2",
        venueId: null,
        fromDate: "",
        fromTime: "",
        toDate: "",
        toTime: "",
      },
    ],
  });

  const getVenueName = (venueId: number | null) => {
    return venuesFromApi.find((v) => v.venueId === venueId)?.venueName || "-";
  };

  useEffect(() => {
    const loadLawazimConfigs = async () => {
      try {
        setLoading(true);
        const data = await getLawazimList({ pageNumber: 1, pageSize: 500 });
        setLawazimConfigs(
          (data.items || []).filter((item: LawazimConfig) => item.isActive),
        );
      } catch (error) {
        console.error("Failed to load Lawazim cancellation policies", error);
      } finally {
        setLoading(false);
      }
    };
    loadLawazimConfigs();
  }, []);

  const mapTransportMode = (mode: "flight" | "road" | "sea") => {
    if (mode === "flight") return 1;
    if (mode === "road") return 2;
    if (mode === "sea") return 3;
    return 1;
  };

  const toISOStringWithTime = (date: string, time: string) => {
    if (!date || !time) return "";
    const local = new Date(`${date}T${time}`);
    return new Date(
      local.getTime() - local.getTimezoneOffset() * 60000,
    ).toISOString();
  };

  const isDateDisabled = (date: Date, type: "arrival" | "departure") => {
    const current = new Date(date);
    current.setHours(0, 0, 0, 0);

    if (selectedMiqaat?.type === "package") {
      const rotationStart = new Date(selectedMiqaat.arrivalDate.split("T")[0]);

      const rotationEnd = new Date(selectedMiqaat.departureDate.split("T")[0]);

      rotationStart.setHours(0, 0, 0, 0);
      rotationEnd.setHours(0, 0, 0, 0);

      return current < rotationStart || current > rotationEnd;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const minBookingDate = new Date(today);
    minBookingDate.setDate(today.getDate() + 3);

    if (current < minBookingDate) {
      return true;
    }

    if (operatorExpiryDate) {
      const expiry = new Date(operatorExpiryDate);
      expiry.setHours(0, 0, 0, 0);

      if (current > expiry) {
        return true;
      }
    }

    return false;
  };

  const formatLocalDate = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const getTourType = () => {
    if (selectedMiqaat?.type === "package") {
      return TourType.MIQAAT;
    }
    return TourType.GENERAL;
  };

  const getRotationId = () => {
    if (selectedMiqaat?.type === "package") {
      return selectedMiqaat.rotationId;
    }
    return null;
  };

  const getTourCategory = () => {
    return getTourType() === TourType.MIQAAT ? 2 : 1;
  };

  useEffect(() => {
    if (currentStep !== 4) return;
    const loadLatestTermsAndConditions = async () => {
      try {
        let code = "";
        const category = getTourCategory();
        switch (category) {
          case 2:
            code = "TOUR_TNC_Miqaat";
            break;

          default:
            code = "TOUR_TNC_Instant";
            break;
        }
        const res = await getGeneralMasterByCode(code);
        setFormData((prev) => ({
          ...prev,
          termsAndConditions: res.value,
        }));
      } catch (error) {
        console.error("Failed to refresh T&C", error);
      }
    };

    loadLatestTermsAndConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    const loadTermsAndConditions = async () => {
      try {
        let code = "";
        const category = getTourCategory();
        switch (category) {
          case 2:
            code = "TOUR_TNC_Miqaat";
            break;
          default:
            code = "TOUR_TNC_Instant";
            break;
        }
        const res = await getGeneralMasterByCode(code);
        setTermsAndConditions(res.value);
        setFormData((prev) => ({
          ...prev,
          termsAndConditions: res.value,
        }));
      } catch (error) {
        console.error("Failed to load T&C", error);
      }
    };
    loadTermsAndConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMiqaat]);

  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const data = await getPorts();
        setPorts(data.filter((p) => p.isActive));
      } catch (e) {
        console.error("Port fetch failed", e);
      }
    };
    fetchPorts();
  }, []);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const data = await getVenues();
        setVenuesFromApi(data.filter((v) => v.isActive));
      } catch (e) {
        console.error("Venue fetch failed", e);
      }
    };

    fetchVenues();
  }, []);

  useEffect(() => {
    const loadProperties = async () => {
      try {
        const data = await getProperties();
        setProperties(data.filter((v) => v.isActive));
      } catch (e) {
        console.error("Venue fetch failed", e);
      }
    };
    loadProperties();
  }, []);

  useEffect(() => {
    setIsVenueManuallyChanged(false);
  }, [formData.arrivalAirport, formData.departureAirport]);

  useEffect(() => {
    if (formData.venues.some((v) => !v.venueId)) return;
    if (isVenueManuallyChanged) return;
    if (
      !isValidTime(formData.arrivalTime) ||
      !isValidTime(formData.departureTime)
    ) {
      return;
    }

    const updatedVenues = calculateVenueSchedule({
      venues: formData.venues,
      arrivalDate: formData.arrivalDate,
      arrivalTime: formData.arrivalTime,
      departureDate: formData.departureDate,
      departureTime: formData.departureTime,
      buffers,
      intercityCheckouts,
      tourType: getTourType(),
    });
    if (!updatedVenues) return;
    const isSame =
      JSON.stringify(formData.venues) === JSON.stringify(updatedVenues);
    if (isSame) return;
    setFormData((prev) => ({
      ...prev,
      venues: prev.venues.map((v, i) => ({
        ...v, // keep id
        ...updatedVenues[i], // override calculated fields
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.arrivalDate,
    formData.arrivalTime,
    formData.departureDate,
    formData.departureTime,
    buffers,
    intercityCheckouts,
    formData.venues,
  ]);

  useEffect(() => {
    const fetchBuffers = async () => {
      try {
        const data = await getBufferConfigurations();
        setBuffers(data.filter((b) => b.isActive));
      } catch (e) {
        console.error("Buffer fetch failed", e);
      }
    };
    fetchBuffers();
  }, []);

  useEffect(() => {
    const fetchIntercity = async () => {
      try {
        const data = await getIntercityCheckouts();
        setIntercityCheckouts(data.filter((i) => i.isActive));
      } catch (e) {
        console.error("Intercity fetch failed", e);
      }
    };
    fetchIntercity();
  }, []);

  useEffect(() => {
    const fetchCutoffs = async () => {
      try {
        const data = await getCutoffConfigurations();
        setCutoffs(data);
      } catch (e) {
        console.error("Cutoff fetch failed", e);
      }
    };
    fetchCutoffs();
  }, []);

  useEffect(() => {
    if (!cutoffs.length) {
      setCutoffDates([]);
      return;
    }
    const dates: string[] = [];
    const formatDate = (date: Date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    cutoffs.forEach((c) => {
      const effectiveFrom = new Date(c.effectiveFrom.split("T")[0]);
      effectiveFrom.setHours(0, 0, 0, 0);

      const effectiveTo = new Date(c.effectiveTo.split("T")[0]);
      effectiveTo.setHours(0, 0, 0, 0);
      if (today < effectiveFrom || today > effectiveTo) {
        return;
      }
      const cutoffEndDate = new Date(today);
      cutoffEndDate.setDate(today.getDate() + c.daysBeforeCutoff - 1);
      if (cutoffEndDate > effectiveTo) {
        cutoffEndDate.setTime(effectiveTo.getTime());
      }
      const current = new Date(today);
      while (current <= cutoffEndDate) {
        dates.push(formatDate(current));
        current.setDate(current.getDate() + 1);
      }
    });
    const uniqueDates = [...new Set(dates)];
    setCutoffDates(uniqueDates);
  }, [cutoffs]);

  useEffect(() => {
    const fetchAllPortBlocks = async () => {
      try {
        if (!ports.length) return;
        const results = await Promise.all(
          ports.map((port) => getPortByPublicId(port.publicId)),
        );
        const allBlocks: Record<number, Date[]> = {};
        results.forEach((result, index) => {
          const port = ports[index];
          const dates: Date[] = [];
          result.blocks.forEach((block) => {
            const start = new Date(block.fromDate.split("T")[0]);
            const end = new Date(block.toDate.split("T")[0]);
            // eslint-disable-next-line prefer-const
            let current = new Date(start);
            while (current <= end) {
              dates.push(
                new Date(
                  current.getFullYear(),
                  current.getMonth(),
                  current.getDate(),
                ),
              );
              current.setDate(current.getDate() + 1);
            }
          });
          allBlocks[port.portId] = dates;
        });
        setPortBlocks(allBlocks);
      } catch (e) {
        console.error(e);
      }
    };
    fetchAllPortBlocks();
  }, [ports]);

  useEffect(() => {
    const fetchOperator = async () => {
      try {
        if (!tourOperatorId) return;

        const res = await getTourOperatorWithDocuments(tourOperatorId);

        const mainOperator = res.partners?.find((p) => p.isMainOperator);

        if (mainOperator) {
          // Save the Tour Operator Type Id
          setTourOperatorTypeId(mainOperator.tourOperatorTypeId);

          // Save the permission expiry date
          if (mainOperator.permissionExpireDate) {
            const [datePart] = mainOperator.permissionExpireDate.split(" ");
            const [mm, dd, yyyy] = datePart.split("/").map(Number);
            const parsed = new Date(yyyy, mm - 1, dd);
            setOperatorExpiryDate(parsed);
          }
        }
      } catch (e) {
        console.error("Failed to fetch operator", e);
      }
    };

    fetchOperator();
  }, [tourOperatorId]);

  useEffect(() => {
    if (currentStep !== 3) return;
    const applicants = Number(formData.noOfApplicants || 0);
    const totalRequestedRooms = roomAvailability.reduce(
      (sum, ra) => sum + ra.requested,
      0,
    );
    if (totalRequestedRooms === 0) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated.capacity;
        return updated;
      });
      return;
    }

    const capacity = getTotalRequestedCapacity();
    setErrors((prev) => {
      const updated = { ...prev };
      if (applicants >= capacity.min && applicants <= capacity.max) {
        delete updated.capacity;
      } else if (applicants < capacity.min) {
        updated.capacity = `Selected room capacity exceeds ${applicants} pax. Please reduce room selection.`;
      } else {
        updated.capacity = `To Proceed - Please select enough number of rooms to accommodate ${applicants} pax.`;
      }
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomAvailability, formData.noOfApplicants, currentStep]);

  useEffect(() => {
    if (currentStep !== 3) return;
    const venueIds = formData.venues.map((v) => v.venueId).filter(Boolean);
    const hasMissingVenue = venueIds.some((venueId) => {
      return !roomAvailability.some(
        (r) => Number(r.venue) === Number(venueId) && Number(r.requested) > 0,
      );
    });
    setErrors((prev) => {
      const updated = { ...prev };
      if (hasMissingVenue) {
        updated.rooms =
          "Please select at least one room for each venue before proceeding.";
      } else {
        delete updated.rooms;
      }
      return updated;
    });
  }, [currentStep, roomAvailability, formData.venues]);

  useEffect(() => {
    setManualDateEdit(false);
  }, [
    formData.arrivalDate,
    formData.arrivalTime,
    formData.departureDate,
    formData.departureTime,
  ]);

  const getBaseRoute = (arrivalPortId: number) => {
    const najafVenue = venuesFromApi.find((v) =>
      v.venueName.toLowerCase().includes("najaf"),
    )?.venueId;

    const karbalaVenue = venuesFromApi.find((v) =>
      v.venueName.toLowerCase().includes("karbala"),
    )?.venueId;

    if (!najafVenue || !karbalaVenue) return [];

    const arrivalPort = ports.find((p) => p.portId === arrivalPortId);

    if (!arrivalPort) return [];

    const isNajafArrival = arrivalPort.portName.toLowerCase().includes("najaf");

    return isNajafArrival
      ? [najafVenue, karbalaVenue]
      : [karbalaVenue, najafVenue];
  };

  useEffect(() => {
    if (!venuesFromApi.length) return;
    if (isVenueManuallyChanged) return;
    if (!formData.arrivalAirport) return;
    const route = getBaseRoute(formData.arrivalAirport);
    if (!route.length) return;
    setFormData((prev) => {
      const updatedVenues = prev.venues.map((v, i) => ({
        ...v,
        venueId: i < route.length ? route[i] : v.venueId,
      }));
      return {
        ...prev,
        venues: updatedVenues,
      };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.arrivalAirport,
    venuesFromApi.length,
    transportMode,
    isVenueManuallyChanged,
  ]);

  const getFilteredPorts = (
    mode: "flight" | "road" | "sea",
    type: "arrival" | "departure",
  ) => {
    return ports.filter((p) => {
      const mappedType = mapTypeFromApi(p.portType);
      if (mode === "flight" && mappedType !== "Air") return false;
      if (mode === "sea" && mappedType !== "Sea") return false;
      if (mode === "road" && mappedType !== "Road") return false;
      const selectedDate = type === "arrival" ? arrivalDate : departureDate;
      if (!selectedDate) return true;
      const blockedDates = portBlocks[p.portId] || [];
      const [y, m, d] = selectedDate.split("-").map(Number);
      const selected = new Date(y, m - 1, d);
      const isBlocked = blockedDates.some(
        (bd) =>
          bd.getFullYear() === selected.getFullYear() &&
          bd.getMonth() === selected.getMonth() &&
          bd.getDate() === selected.getDate(),
      );
      return !isBlocked;
    });
  };

  // Initialize room availability when venues are configured
  useEffect(() => {
    const rooms: RoomAvailability[] = [];
    availabilityData.forEach((venue) => {
      venue.properties.forEach((property) => {
        property.roomTypes.forEach((room) => {
          rooms.push({
            venue: venue.venueId,
            propertyId: property.propertyId.toString(),
            propertyName: property.propertyName,
            checkInDate: venue.checkInDate.split("T")[0],
            checkOutDate: venue.checkOutDate.split("T")[0],
            roomType: room.propertyRoomTypeId.toString(),
            roomTypeName: room.roomTypeName,
            minCapacity: room.minCapacity,
            maxCapacity: room.maxCapacity,
            overallAvailable: room.overallAvailableRooms,
            requested: 0,
            dailyAvailability: [],
          });
        });
      });
    });
    setRoomAvailability(rooms);
  }, [availabilityData]);

  useEffect(() => {
    console.log(
      "ROOM AVAILABILITY STATE",
      roomAvailability.map((r) => ({
        venue: r.venue,
        room: r.roomTypeName,
        requested: r.requested,
      })),
    );
  }, [roomAvailability]);

  const getDatesInRange = (startDate: string, endDate: string) => {
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);

    const current = new Date(start);
    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const handleRoomRequestUpdate = (
    venueId: number | null,
    propertyId: string,
    roomType: string,
    checkInDate: string,
    checkOutDate: string,
    value: number,
  ) => {
    console.log("ROOM UPDATE", venueId, propertyId, roomType, value);
    setRoomAvailability((prev) =>
      prev.map((ra) => {
        if (
          ra.venue === venueId &&
          ra.propertyId === propertyId &&
          ra.roomType === roomType &&
          ra.checkInDate === checkInDate &&
          ra.checkOutDate === checkOutDate
        ) {
          return {
            ...ra,
            requested: value,
          };
        }

        return ra;
      }),
    );
    console.log("Updated value:", value);
  };

  const getTotalRequestedCapacity = () => {
    return roomAvailability.reduce(
      (acc, ra) => {
        acc.min += ra.requested * ra.minCapacity;
        acc.max += ra.requested * ra.maxCapacity;
        return acc;
      },
      { min: 0, max: 0 },
    );
  };

  const togglePropertyExpand = (propertyKey: string) => {
    setExpandedProperties((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(propertyKey)) {
        newSet.delete(propertyKey);
      } else {
        newSet.add(propertyKey);
      }
      return newSet;
    });
  };

  const FLIGHT_NO_REGEX = /^(?:[A-Z]{2}|[A-Z][0-9])[0-9]{1,4}$/;
  const isValidFlightNumber = (flightNo: string) =>
    FLIGHT_NO_REGEX.test(flightNo);

  const TIME_24H_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;
  const isValidTime = (value: string) => {
    return TIME_24H_REGEX.test(value);
  };

  const AIRLINE_REGEX = /^[\p{L}\p{M}\p{N}\s-]{1,60}$/u;
  const isValidAirline = (value: string) => AIRLINE_REGEX.test(value.trim());

  const BOOKING_REF_REGEX = /^[A-Z0-9]{1,16}$/;
  const isValidBookingRef = (value: string) => BOOKING_REF_REGEX.test(value);

  const TOUR_NAME_REGEX = /^[A-Za-z0-9 ]{1,50}$/;

  const PARENT_TOUR_ID_REGEX = /^[A-Za-z0-9-]{1,50}$/;

  const isValidParentTourId = (value: string) =>
    PARENT_TOUR_ID_REGEX.test(value);

  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {};

    // STEP 1
    if (step === 1) {
      // ==========================
      // ARRIVAL
      // ==========================
      if (!formData.arrivalDate) {
        newErrors.arrivalDate = "Arrival date is required";
      }

      if (!formData.arrivalTime) {
        newErrors.arrivalTime = "Arrival time is required";
      } else if (!isValidTime(formData.arrivalTime)) {
        newErrors.arrivalTime = "Time must be in 24-hour format (HH:MM)";
      }

      if (!formData.arrivalAirport) {
        newErrors.arrivalAirport =
          formData.arrivalTransportMode === "flight"
            ? "Arrival airport is required"
            : "Arrival port is required";
      }

      if (formData.arrivalTransportMode === "flight") {
        if (!formData.arrivalFlightNumber) {
          newErrors.arrivalFlightNumber = "Flight No. is required";
        } else if (!isValidFlightNumber(formData.arrivalFlightNumber)) {
          newErrors.arrivalFlightNumber = "Format must be AB1234 or A11234";
        }

        if (
          formData.arrivalAirline &&
          !isValidAirline(formData.arrivalAirline)
        ) {
          newErrors.arrivalAirline =
            "Only letters, numbers, spaces and hyphens are allowed.";
        }

        if (
          formData.arrivalBookingRef &&
          !isValidBookingRef(formData.arrivalBookingRef)
        ) {
          newErrors.arrivalBookingRef =
            "Booking Ref must be alphanumeric and max 16 characters";
        }
      }

      // ==========================
      // DEPARTURE
      // ==========================
      if (!formData.departureDate) {
        newErrors.departureDate = "Departure date is required";
      }

      if (!formData.departureTime) {
        newErrors.departureTime = "Departure time is required";
      } else if (!isValidTime(formData.departureTime)) {
        newErrors.departureTime = "Time must be in 24-hour format (HH:MM)";
      }

      if (!formData.departureAirport) {
        newErrors.departureAirport =
          formData.departureTransportMode === "flight"
            ? "Departure airport is required"
            : "Departure port is required";
      }

      if (formData.departureTransportMode === "flight") {
        if (!formData.departureFlightNumber) {
          newErrors.departureFlightNumber = "Flight No. is required";
        } else if (!isValidFlightNumber(formData.departureFlightNumber)) {
          newErrors.departureFlightNumber = "Format must be AB1234 or A11234";
        }

        if (
          formData.departureAirline &&
          !isValidAirline(formData.departureAirline)
        ) {
          newErrors.departureAirline =
            "Only letters, numbers, spaces and hyphens are allowed.";
        }

        if (
          formData.departureBookingRef &&
          !isValidBookingRef(formData.departureBookingRef)
        ) {
          newErrors.departureBookingRef =
            "Booking Ref must be alphanumeric and max 16 characters";
        }
      }
    }

    // STEP 2
    if (step === 2) {
      if (formData.venues.length === 0) {
        newErrors.venues = "Add at least one venue";
      }

      if (!formData.tourName.trim()) {
        newErrors.tourName = "Tour name is required";
      } else if (!TOUR_NAME_REGEX.test(formData.tourName.trim())) {
        newErrors.tourName =
          "Only letters and numbers allowed. Maximum 50 characters.";
      }

      if (
        formData.parentTourId.trim() &&
        !isValidParentTourId(formData.parentTourId.trim())
      ) {
        newErrors.parentTourId =
          "Only letters, numbers and hyphens (-) are allowed. Maximum 50 characters.";
      }

      if (!formData.noOfApplicants) {
        newErrors.noOfApplicants = "Applicants required";
      } else {
        const applicants = Number(formData.noOfApplicants);

        if (applicants < 0) {
          newErrors.noOfApplicants = "Number of applicants cannot be negative";
        } else if (applicants > 200) {
          newErrors.noOfApplicants = "Number of applicants cannot exceed 200";
        }
      }

      formData.venues.forEach((venue, index) => {
        if (!venue.venueId) {
          newErrors[`venue-${index}`] = "Venue is required";
        }
        if (!venue.fromDate) {
          newErrors[`fromDate-${index}`] = "Check-in date required";
        }
        if (!venue.toDate) {
          newErrors[`toDate-${index}`] = "Check-out date required";
        }
        if (!venue.fromTime) {
          newErrors[`fromTime-${index}`] = "Check-in time required";
        }
        if (!venue.toTime) {
          newErrors[`toTime-${index}`] = "Check-out time required";
        }
      });
      for (let index = 1; index < formData.venues.length; index++) {
        const previousVenue = formData.venues[index - 1];
        const currentVenue = formData.venues[index];

        if (
          previousVenue.venueId &&
          currentVenue.venueId &&
          previousVenue.venueId === currentVenue.venueId
        ) {
          const venueName =
            venuesFromApi.find((v) => v.venueId === currentVenue.venueId)
              ?.venueName || "This venue";

          toast.error(
            `Venue ${index + 1} cannot be the same as Venue ${index}. Please select a different venue instead of ${venueName}.`,
          );

          return false;
        }
      }
    }

    // STEP 3
    if (step === 3) {
      const venueIds = formData.venues.map((v) => v.venueId).filter(Boolean);

      const venueRoomMap = venueIds.map((venueId) => ({
        venueId,
        rooms: roomAvailability.filter(
          (r) => Number(r.venue) === Number(venueId) && Number(r.requested) > 0,
        ),
      }));

      const hasMissingVenue = venueRoomMap.some((v) => v.rooms.length === 0);

      if (hasMissingVenue) {
        newErrors.rooms =
          "Please select at least one room for each venue before proceeding.";
      }

      console.log("venueRoomMap", venueRoomMap);
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (transportMode !== "road") return;
    if (!ports.length) return;

    const roadPorts = ports.filter(
      (p) => mapTypeFromApi(p.portType) === "Road",
    );

    if (roadPorts.length < 2) return;

    const arrivalPortId = roadPorts[0]?.portId;
    const departurePortId = roadPorts[1]?.portId;

    setFormData((prev) => {
      // already set correctly
      if (
        prev.arrivalAirport === arrivalPortId &&
        prev.departureAirport === departurePortId
      ) {
        return prev;
      }

      return {
        ...prev,

        arrivalAirport: arrivalPortId,
        departureAirport: departurePortId,

        // IMPORTANT
        arrivalTime: prev.arrivalTime || "",
        departureTime: prev.departureTime || "",
      };
    });
  }, [transportMode, ports]);

  useEffect(() => {
    // ARRIVAL PORT AUTO SELECT
    const arrivalPorts = getFilteredPorts(
      formData.arrivalTransportMode,
      "arrival",
    );

    if (arrivalPorts.length === 1 && !formData.arrivalAirport) {
      setFormData((prev) => ({
        ...prev,
        arrivalAirport: arrivalPorts[0].portId,
      }));
    }

    // DEPARTURE PORT AUTO SELECT
    const departurePorts = getFilteredPorts(
      formData.departureTransportMode,
      "departure",
    );

    if (departurePorts.length === 1 && !formData.departureAirport) {
      setFormData((prev) => ({
        ...prev,
        departureAirport: departurePorts[0].portId,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ports,
    formData.arrivalTransportMode,
    formData.departureTransportMode,
    formData.arrivalAirport,
    formData.departureAirport,
    formData.arrivalDate,
    formData.departureDate,
  ]);

  const handleNext = async () => {
    const isValid = validateStep(currentStep);

    if (!isValid) {
      return;
    }

    setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);

    if (currentStep === 2) {
      if (!tourOperatorId) {
        toast.error("Tour operator not found");
        return;
      }
      try {
        setLoading(true);
        const payload = {
          noOfApplicants: Number(formData.noOfApplicants),

          tourOperatorTypeId: Number(tourOperatorTypeId),

          venueSchedules: formData.venues
            .filter((v) => v.venueId)
            .map((v) => ({
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              venueId: v.venueId!,
              checkInDate: toISOStringWithTime(v.fromDate, v.fromTime),
              checkInTime: v.fromTime,
              checkOutDate: toISOStringWithTime(v.toDate, v.toTime),
              checkOutTime: v.toTime,
            })),
        };

        const result = await getInstantBookingAvailability(payload);

        setAvailabilityData(result);

        setCurrentStep(3);

        return;
      } catch (e) {
        console.error(e);
        toast.error("Failed to load room availability");
        return;
      } finally {
        setLoading(false);
      }
    }

    if (currentStep === 3) {
      try {
        let code = "";

        const category = getTourCategory();

        switch (category) {
          case 2:
            code = "TOUR_TNC_Miqaat";
            break;

          default:
            code = "TOUR_TNC_Instant";
            break;
        }

        const res = await getGeneralMasterByCode(code);

        setFormData((prev) => ({
          ...prev,
          termsAndConditions: res.value,
        }));
      } catch (error) {
        console.error("Failed to refresh T&C", error);
      }
    }

    if (currentStep < 4) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleFinalConfirmation = async () => {
    try {
      setLoading(true);
      if (!tourOperatorId) {
        toast.error("Tour operator not found");
        return;
      }

      const roomAvailabilities = availabilityData.map((venue) => ({
        venueId: venue.venueId,
        checkInDate: venue.checkInDate,
        checkOutDate: venue.checkOutDate,

        properties: venue.properties
          .map((property) => ({
            propertyId: property.propertyId,

            rooms: property.roomTypes
              .map((roomType) => {
                const selectedRoom = roomAvailability.find(
                  (r) =>
                    Number(r.venue) === venue.venueId &&
                    Number(r.propertyId) === property.propertyId &&
                    Number(r.roomType) === roomType.propertyRoomTypeId,
                );

                return {
                  propertyRoomTypeId: roomType.propertyRoomTypeId,
                  requestedNumber: selectedRoom?.requested || 0,
                };
              })
              .filter((room) => room.requestedNumber > 0),
          }))
          .filter((property) => property.rooms.length > 0),
      }));

      const payload = {
        parent_id: formData.parentTourId.trim() || null,
        tourName: formData.tourName,
        tourType: getTourType(),
        tourCategory: getTourCategory(),
        rotationId: getRotationId(),
        tourOperatorId: tourOperatorId,
        noOfApplicants: Number(formData.noOfApplicants),
        faizAccommodation: formData.faizAccommodation === "yes",
        termsAndConditions: formData.termsAndConditions,
        arrivalTransportMode: mapTransportMode(formData.arrivalTransportMode),
        arrivalFlightNo: formData.arrivalFlightNumber || "",
        arrivalTime: `${formData.arrivalTime}:00`,
        arrivalPortId: formData.arrivalAirport,
        arrivalDate: toISOStringWithTime(
          formData.arrivalDate,
          formData.arrivalTime,
        ),
        arrivalBookingRef: formData.arrivalBookingRef || "",
        arrivalAirline: formData.arrivalAirline || "",

        departureTransportMode: mapTransportMode(
          formData.departureTransportMode,
        ),
        departureFlightNo: formData.departureFlightNumber || "",
        departureTime: `${formData.departureTime}:00`,
        departurePortId: formData.departureAirport,
        departureDate: toISOStringWithTime(
          formData.departureDate,
          formData.departureTime,
        ),
        departureBookingRef: formData.departureBookingRef || "",
        departureAirline: formData.departureAirline || "",

        isArrivalConfirmed: formData.arrivalConfirmed,
        isDepartureConfirmed: formData.departureConfirmed,

        venueSchedules: formData.venues
          .filter((v): v is typeof v & { venueId: number } => v.venueId != null)
          .map((v) => ({
            venueId: v.venueId,
            checkInDate: toISOStringWithTime(v.fromDate, v.fromTime),
            checkInTime: `${v.fromTime}:00`,
            checkOutDate: toISOStringWithTime(v.toDate, v.toTime),
            checkOutTime: `${v.toTime}:00`,
          })),

        roomAvailabilities,

        createdBy: Number(user?.id),
      };

      console.log("CREATE TOUR PAYLOAD", JSON.stringify(payload, null, 2));

      const res = await createTour(payload);

      toast.success(`Tour created successfully! Tour Code: ${res.tourCode}`);
      navigate("/tours");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to create tour";
      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const canAddVenue = () => {
    if (
      !formData.arrivalDate ||
      !formData.arrivalTime ||
      !formData.departureDate ||
      !formData.departureTime
    ) {
      return false;
    }

    if (formData.venues.some((v) => !v.venueId)) {
      return false;
    }

    return canAccommodateAnotherVenue({
      venues: formData.venues,
      arrivalDate: formData.arrivalDate,
      arrivalTime: formData.arrivalTime,
      departureDate: formData.departureDate,
      departureTime: formData.departureTime,
      buffers,
      intercityCheckouts,
      tourType: getTourType(),
    });
  };

  const getCalculationBreakdown = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    venue: any,
    index: number,
    type: "checkin" | "checkout",
  ) => {
    if (!venue.venueId) return null;

    const venueId = venue.venueId;

    const venueName =
      venuesFromApi.find((v) => v.venueId === venueId)?.venueName || "";

    const isLast = index === formData.venues.length - 1;

    let portId: number | undefined;

    if (index === 0) {
      portId = formData.arrivalAirport;
    } else if (isLast) {
      portId = formData.departureAirport;
    } else {
      portId = undefined;
    }

    let intercityTime: string | null = null;

    if (type === "checkout" && !isLast) {
      const venueDate = new Date(venue.toDate);

      const nextVenue = formData.venues[index + 1];

      const intercity = nextVenue?.venueId
        ? getIntercityCheckout(
            intercityCheckouts,
            venueId,
            nextVenue.venueId,
            venueDate,
            getTourType(),
          )
        : null;

      if (intercity) {
        intercityTime = intercity.checkoutTime;
      }
    }

    const isIntercityCase = type === "checkout" && !!intercityTime;

    let hasTravel = false;

    if (type === "checkin" && index > 0) {
      const prevVenue = formData.venues[index - 1];

      if (prevVenue?.venueId) {
        const intercity = getIntercityCheckout(
          intercityCheckouts,
          prevVenue.venueId,
          venue.venueId,
          new Date(prevVenue.toDate),
          getTourType(),
        );

        if (intercity?.travelTime) {
          hasTravel = true;
        }
      }
    }

    const buffersData = getTotalVenueBuffer(
      buffers,
      venueId,
      portId,
      !(isIntercityCase || hasTravel),
    );

    const accommodationBuffers = buffers.filter(
      (b) => b.venueId === venueId && b.type === "1",
    );

    const transportBuffers = !(isIntercityCase || hasTravel)
      ? buffers.filter((b) => b.venueId === venueId && b.type === "2")
      : [];

    const sumBuffers = (
      arr: BufferConfig[],
      key: "checkInBuffer" | "checkOutBuffer",
    ) =>
      arr.reduce(
        (total, b) => total + parseTimeToMinutes(b[key] || "00:00"),
        0,
      );

    const formatHours = (minutes: number) => `${Math.floor(minutes / 60)} hrs`;

    const accommodationMinutes = sumBuffers(
      accommodationBuffers,
      type === "checkin" ? "checkInBuffer" : "checkOutBuffer",
    );

    const transportMinutes = sumBuffers(
      transportBuffers,
      type === "checkin" ? "checkInBuffer" : "checkOutBuffer",
    );

    let travelMinutes = 0;

    if (type === "checkin" && index > 0) {
      const prevVenue = formData.venues[index - 1];

      if (prevVenue?.venueId) {
        const intercity =
          prevVenue?.venueId && venue.venueId
            ? getIntercityCheckout(
                intercityCheckouts,
                prevVenue.venueId,
                venue.venueId,
                new Date(prevVenue.toDate),
                getTourType(),
              )
            : null;

        if (intercity?.travelTime) {
          travelMinutes = getTravelTimeMinutes(intercity.travelTime);
        }
      }
    }

    const totalMinutes =
      (isIntercityCase
        ? accommodationMinutes
        : type === "checkin"
          ? buffersData.checkIn
          : buffersData.checkOut) + travelMinutes;

    const rawBaseTime =
      type === "checkin"
        ? index === 0
          ? formData.arrivalTime
          : venue.fromTime
        : isLast
          ? formData.departureTime
          : intercityTime
            ? intercityTime
            : venue.toTime;

    const baseTime = normalizeTime(rawBaseTime || "");

    return {
      venueName,
      baseTime,
      accommodation: formatHours(accommodationMinutes),
      transport: formatHours(transportMinutes),
      totalBuffer: formatHours(totalMinutes),
      hasTransport: !isIntercityCase && transportMinutes > 0,
      usedIntercity: isIntercityCase,
      travel: travelMinutes ? formatHours(travelMinutes) : null,
    };
  };

  const CalculationBox = ({
    data,
    type,
    index,
    isLast,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
    type: "checkin" | "checkout";
    index: number;
    isLast: boolean;
  }) => {
    if (!data) return null;
    const isFirst = index === 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isMiddle = !isFirst && !isLast;
    let label: string | null = null;
    if (type === "checkin") {
      if (isFirst) {
        label = "Arrival Time";
      } else {
        label = null;
      }
    } else {
      // checkout
      if (data.usedIntercity) {
        label = "Intercity Checkout";
      } else if (isLast) {
        label = "Departure Time";
      } else {
        label = null;
      }
    }

    return (
      <div
        className="absolute z-10 left-0 mt-2 w-64 p-3 rounded-md bg-blue-50 border border-blue-200 text-xs text-gray-700 space-y-1 
        opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 shadow-lg pointer-events-none"
      >
        <div className="font-medium text-blue-700">Calculation Breakdown</div>
        {label && (
          <div>
            {label}: <span className="font-semibold">{data.baseTime}</span>
          </div>
        )}

        <div>
          Accommodation Buffer:{" "}
          <span className="font-semibold">{data.accommodation}</span>
        </div>

        {data.hasTransport && (
          <div>
            Transport Buffer:{" "}
            <span className="font-semibold">{data.transport}</span>
          </div>
        )}

        {data.travel && (
          <div>
            Travel Time: <span className="font-semibold">{data.travel}</span>
          </div>
        )}

        <div className="pt-1 border-t">
          Total Buffer:{" "}
          <span className="font-semibold text-blue-700">
            {data.totalBuffer}
          </span>
        </div>
      </div>
    );
  };

  const isOutsideTourRange = (date: Date) => {
    if (!formData.arrivalDate || !formData.departureDate) return false;

    const d = new Date(date);
    const start = new Date(formData.arrivalDate);
    const end = new Date(formData.departureDate);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);

    return d < start || d > end;
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="min-h-screen bg-background pb-6">
        {/* Main Content */}
        <div>
          {/* Progress Indicator */}
          {/* Tabs */}
          <div className="mb-4 flex w-full min-w-0 flex-col gap-3 border-b border-border sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 overflow-x-auto">
              {[
                { step: 1, label: "Arrival & Departure" },
                { step: 2, label: "Venue Summary" },
                { step: 3, label: "Available Rooms" },
                { step: 4, label: "Confirmation" },
              ].map(({ step, label }) => {
                const isDisabled = step !== currentStep;

                return (
                  <button
                    key={step}
                    disabled={isDisabled}
                    className={`flex items-center gap-2 px-4 py-3 relative text-sm whitespace-nowrap transition-all ${
                      isDisabled
                        ? "opacity-50 cursor-not-allowed"
                        : currentStep === step
                          ? "text-primary"
                          : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold text-xs ${
                        currentStep === step
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {step}
                    </div>

                    <span className="font-medium">{label}</span>

                    {currentStep === step && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mb-2 flex shrink-0 items-center justify-end px-2 sm:mb-0 sm:pl-3">
              <TourTypeSwitcher value={1} onChange={handleTourTypeChange} />
            </div>
          </div>

          {/* Tab Content */}
          <div className="">
            <div className="">
              {/* Tab 1: Arrival & Departure */}
              {currentStep === 1 && (
                <div className="space-y-5">
                  {/* Transport Mode Selection */}
                  <Card>
                    <CardContent className="p-6 bg-white rounded-xl">
                      <div className="flex items-center gap-4">
                        <Label className="text-sm font-semibold text-gray-900">
                          Select Transport Mode{" "}
                          <span className="text-red-500">*</span>
                        </Label>

                        <RadioGroup
                          value={transportMode}
                          onValueChange={(value: "flight" | "road") => {
                            setTransportMode(value);

                            setFormData((prev) => ({
                              ...prev,

                              arrivalTransportMode: value,
                              departureTransportMode: value,

                              // reset ports when changing mode
                              arrivalAirport: 0,
                              departureAirport: 0,

                              // clear flight-only fields
                              arrivalFlightNumber: "",
                              arrivalBookingRef: "",
                              arrivalAirline: "",
                              arrivalTime: "",
                              arrivalConfirmed: false,

                              departureFlightNumber: "",
                              departureBookingRef: "",
                              departureAirline: "",
                              departureTime: "",
                              departureConfirmed: false,
                            }));
                          }}
                          className="flex gap-6 items-center"
                        >
                          {/* Flight */}
                          <div className="flex items-center gap-2">
                            <RadioGroupItem
                              value="flight"
                              id="transport-flight"
                              className="
                                  h-5 w-5 rounded-full
                                  border-2 border-gray-200
                                  flex items-center justify-center
                                  data-[state=checked]:border-primary
                                  "
                            >
                              <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                            </RadioGroupItem>

                            <Label
                              htmlFor="transport-flight"
                              className="text-sm cursor-pointer"
                            >
                              By Flight
                            </Label>
                          </div>

                          {/* Road */}
                          <div className="flex items-center gap-2">
                            <RadioGroupItem
                              value="road"
                              id="transport-road"
                              className="
                                  h-5 w-5 rounded-full
                                  border-2 border-gray-200
                                  flex items-center justify-center
                                  data-[state=checked]:border-primary
                                  "
                            >
                              <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                            </RadioGroupItem>

                            <Label
                              htmlFor="transport-road"
                              className="text-sm cursor-pointer"
                            >
                              By Road
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Arrival and Departure Cards Side by Side */}
                  {transportMode === "flight" && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      {/* Arrival Card */}
                      <Card
                        className="border-2 border-primary/40 shadow-sm bg-white"
                        style={{ gap: "0px" }}
                      >
                        <CardHeader className="bg-card" style={{ gap: "0px" }}>
                          <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                              <ArrowRight className="w-5 h-5 text-primary-foreground" />
                            </div>
                            <CardTitle className="text-base font-semibold text-foreground">
                              Arrival Details
                            </CardTitle>
                          </div>
                        </CardHeader>

                        <CardContent className="space-y-4 pt-4 bg-card">
                          {/* Row 1: Flight No. and Time */}
                          {formData.arrivalTransportMode === "flight" && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <SelectFieldset label="Date" required>
                                  <CustomDayPicker
                                    value={formData.arrivalDate}
                                    operatorExpiryDate={
                                      operatorExpiryDate ?? undefined
                                    }
                                    disabledDates={portBlockedDates}
                                    cutoffDates={cutoffBlockedDates}
                                    blockedDateReasons={blockedDateReasons}
                                    month={arrivalCalendarMonth}
                                    onMonthChange={(month) => {
                                      setArrivalCalendarMonth(month);
                                      setDepartureCalendarMonth(month);
                                    }}
                                    disabled={(date) =>
                                      isDateDisabled(date, "arrival")
                                    }
                                    onChange={(date) => {
                                      const formatted = formatLocalDate(date);
                                      setArrivalCalendarMonth(date);
                                      setDepartureCalendarMonth(date);

                                      setFormData((prev) => ({
                                        ...prev,
                                        arrivalDate: formatted,
                                        departureDate:
                                          prev.departureDate &&
                                          new Date(prev.departureDate) <=
                                            new Date(formatted)
                                            ? ""
                                            : prev.departureDate,
                                      }));
                                      setErrors((prev) => ({
                                        ...prev,
                                        arrivalDate: "",
                                      }));
                                    }}
                                  />
                                </SelectFieldset>
                                {errors.arrivalDate && (
                                  <p className="text-xs text-red-500 mt-1">
                                    {errors.arrivalDate}
                                  </p>
                                )}
                              </div>
                              <div>
                                <InputFieldset
                                  label="Flight No."
                                  required
                                  placeholder="IQ2021"
                                  value={formData.arrivalFlightNumber}
                                  maxLength={6}
                                  onChange={(e) => {
                                    let value = e.target.value.toUpperCase();

                                    value = value.replace(/[^A-Z0-9]/g, "");

                                    // First 2 chars must be:
                                    // AA  OR  A1
                                    if (value.length <= 2) {
                                      if (
                                        !/^([A-Z]{0,2}|[A-Z][0-9]?)$/.test(
                                          value,
                                        )
                                      ) {
                                        return;
                                      }
                                    }

                                    // After first 2 chars only digits allowed
                                    if (value.length > 2) {
                                      const prefix = value.substring(0, 2);
                                      const digits = value.substring(2);

                                      const validPrefix =
                                        /^[A-Z]{2}$/.test(prefix) ||
                                        /^[A-Z][0-9]$/.test(prefix);

                                      if (!validPrefix) {
                                        return;
                                      }

                                      if (!/^\d{0,4}$/.test(digits)) {
                                        return;
                                      }
                                    }

                                    setFormData((prev) => ({
                                      ...prev,
                                      arrivalFlightNumber: value,
                                    }));
                                    setErrors((prev) => ({
                                      ...prev,
                                      arrivalFlightNumber:
                                        value && !isValidFlightNumber(value)
                                          ? "Format must be AB1234 or A11234"
                                          : "",
                                    }));
                                  }}
                                  className="text-xs"
                                />

                                {errors.arrivalFlightNumber && (
                                  <p className="text-xs text-red-500 mt-1">
                                    {errors.arrivalFlightNumber}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Row 2: Date and Airport */}
                          {formData.arrivalTransportMode === "flight" && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <InputFieldset
                                  label="Time (24hr Format)"
                                  required
                                  type="text"
                                  placeholder="HH:MM"
                                  maxLength={5}
                                  className="text-xs"
                                  value={formData.arrivalTime}
                                  onChange={(e) => {
                                    let value = e.target.value.replace(
                                      /[^\d]/g,
                                      "",
                                    );

                                    if (value.length > 4) return;

                                    if (value.length >= 3) {
                                      value = `${value.slice(0, 2)}:${value.slice(2)}`;
                                    }

                                    setFormData((prev) => ({
                                      ...prev,
                                      arrivalTime: value,
                                    }));

                                    setErrors((prev) => ({
                                      ...prev,
                                      arrivalTime:
                                        value.length > 0 && !isValidTime(value)
                                          ? "Time must be in 24-hour format (HH:MM)"
                                          : "",
                                    }));
                                  }}
                                />

                                {errors.arrivalTime && (
                                  <p className="text-xs text-red-500 mt-1">
                                    {errors.arrivalTime}
                                  </p>
                                )}
                              </div>
                              <div>
                                <SelectFieldset
                                  label={
                                    formData.arrivalTransportMode === "flight"
                                      ? "Airport"
                                      : "Port"
                                  }
                                  required
                                >
                                  <Select
                                    key={`${formData.arrivalDate}-${formData.arrivalTransportMode}`}
                                    value={
                                      formData.arrivalAirport
                                        ? formData.arrivalAirport.toString()
                                        : ""
                                    }
                                    onValueChange={(value) => {
                                      const portId = Number(value);
                                      if (!formData.arrivalDate) {
                                        setFormData((prev) => ({
                                          ...prev,
                                          arrivalAirport: portId,
                                        }));
                                        return;
                                      }

                                      setFormData((prev) => ({
                                        ...prev,
                                        arrivalAirport: portId,
                                      }));
                                    }}
                                  >
                                    <SelectTriggerUI className="h-9 text-xs">
                                      <SelectValue placeholder="Select Port" />
                                    </SelectTriggerUI>

                                    <SelectContent>
                                      {getFilteredPorts(
                                        formData.arrivalTransportMode,
                                        "arrival",
                                      ).map((port) => (
                                        <SelectItem
                                          key={port.portId}
                                          value={port.portId.toString()}
                                        >
                                          {port.portName}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </SelectFieldset>
                                {errors.arrivalAirport && (
                                  <p className="text-xs text-red-500 mt-1">
                                    {errors.arrivalAirport}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Row 3: Airline and Booking Ref */}
                          {formData.arrivalTransportMode === "flight" && (
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                              <div>
                                <InputFieldset
                                  label="Airline"
                                  type="text"
                                  placeholder="Enter airline"
                                  className="text-xs"
                                  maxLength={60}
                                  value={formData.arrivalAirline}
                                  onChange={(e) => {
                                    const value = e.target.value;

                                    if (
                                      !/^[\p{L}\p{M}\p{N}\s-]*$/u.test(value)
                                    ) {
                                      return;
                                    }

                                    setFormData((prev) => ({
                                      ...prev,
                                      arrivalAirline: value,
                                    }));
                                  }}
                                />

                                {errors.arrivalAirline && (
                                  <p className="text-xs text-red-500 mt-1">
                                    {errors.arrivalAirline}
                                  </p>
                                )}
                              </div>
                              <div>
                                <InputFieldset
                                  label="Booking Ref"
                                  placeholder="Booking Ref"
                                  className="text-xs"
                                  maxLength={16}
                                  value={formData.arrivalBookingRef}
                                  onChange={(e) => {
                                    const value = e.target.value
                                      .toUpperCase()
                                      .replace(/[^A-Z0-9]/g, "");

                                    setFormData((prev) => ({
                                      ...prev,
                                      arrivalBookingRef: value,
                                    }));
                                  }}
                                  disabled={formData.arrivalConfirmed}
                                />
                                {errors.arrivalBookingRef && (
                                  <p className="text-xs text-red-500 mt-1">
                                    {errors.arrivalBookingRef}
                                  </p>
                                )}
                              </div>
                              <div className="pb-0.5">
                                <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                                  Confirmed
                                </Label>
                                <div className="flex items-center h-9">
                                  <Switch
                                    checked={formData.arrivalConfirmed}
                                    onCheckedChange={(checked) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        arrivalConfirmed: checked,
                                      }))
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Departure Card */}
                      <Card
                        className="border-2 border-orange-400/40 shadow-sm bg-white"
                        style={{ gap: "0px" }}
                      >
                        <CardHeader className="bg-card" style={{ gap: "0px" }}>
                          <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-full bg-orange-600 flex items-center justify-center">
                              <Plane className="w-5 h-5 text-white rotate-45" />
                            </div>
                            <CardTitle className="text-base font-semibold text-foreground">
                              Departure Details
                            </CardTitle>
                          </div>
                        </CardHeader>

                        <CardContent className="space-y-4 pt-4 bg-card">
                          {/* Row 1: Flight No. and Time */}
                          {formData.departureTransportMode === "flight" && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <SelectFieldset label="Date" required>
                                  <CustomDayPicker
                                    value={formData.departureDate}
                                    operatorExpiryDate={
                                      operatorExpiryDate ?? undefined
                                    }
                                    disabledDates={portBlockedDates}
                                    blockedDateReasons={blockedDateReasons}
                                    cutoffDates={cutoffBlockedDates}
                                    minDate={
                                      formData.arrivalDate
                                        ? new Date(formData.arrivalDate)
                                        : undefined
                                    }
                                    month={departureCalendarMonth}
                                    onMonthChange={(month) => {
                                      setDepartureCalendarMonth(month);
                                    }}
                                    // existing validation logic
                                    disabled={(date) =>
                                      isDateDisabled(date, "departure")
                                    }
                                    onChange={(date: Date) => {
                                      const formatted = formatLocalDate(date);

                                      setFormData((prev) => ({
                                        ...prev,
                                        departureDate: formatted,
                                      }));
                                    }}
                                  />
                                </SelectFieldset>
                                {errors.departureDate && (
                                  <p className="text-xs text-red-500 mt-1">
                                    {errors.departureDate}
                                  </p>
                                )}
                              </div>
                              <div>
                                <InputFieldset
                                  label="Flight No."
                                  required
                                  placeholder="IQ2022"
                                  className="text-xs"
                                  value={formData.departureFlightNumber}
                                  maxLength={6}
                                  onChange={(e) => {
                                    let value = e.target.value.toUpperCase();

                                    value = value.replace(/[^A-Z0-9]/g, "");

                                    if (value.length <= 2) {
                                      if (
                                        !/^([A-Z]{0,2}|[A-Z][0-9]?)$/.test(
                                          value,
                                        )
                                      ) {
                                        return;
                                      }
                                    }

                                    if (value.length > 2) {
                                      const prefix = value.substring(0, 2);
                                      const digits = value.substring(2);

                                      const validPrefix =
                                        /^[A-Z]{2}$/.test(prefix) ||
                                        /^[A-Z][0-9]$/.test(prefix);

                                      if (!validPrefix) {
                                        return;
                                      }

                                      if (!/^\d{0,4}$/.test(digits)) {
                                        return;
                                      }
                                    }

                                    setFormData((prev) => ({
                                      ...prev,
                                      departureFlightNumber: value,
                                    }));
                                    setErrors((prev) => ({
                                      ...prev,
                                      departureFlightNumber:
                                        value && !isValidFlightNumber(value)
                                          ? "Format must be AB1234 or A11234"
                                          : "",
                                    }));
                                  }}
                                />
                                {errors.departureFlightNumber && (
                                  <p className="text-xs text-red-500 mt-1">
                                    {errors.departureFlightNumber}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Row 2: Date and Airport */}
                          {formData.departureTransportMode === "flight" && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <InputFieldset
                                  label="Time (24hr Format)"
                                  required
                                  type="text"
                                  placeholder="HH:MM"
                                  maxLength={5}
                                  className="text-xs"
                                  value={formData.departureTime}
                                  onChange={(e) => {
                                    let value = e.target.value.replace(
                                      /[^\d]/g,
                                      "",
                                    );

                                    if (value.length > 4) return;

                                    if (value.length >= 3) {
                                      value = `${value.slice(0, 2)}:${value.slice(2)}`;
                                    }

                                    setFormData((prev) => ({
                                      ...prev,
                                      departureTime: value,
                                    }));

                                    setErrors((prev) => ({
                                      ...prev,
                                      departureTime:
                                        value.length > 0 && !isValidTime(value)
                                          ? "Time must be in 24-hour format (HH:MM)"
                                          : "",
                                    }));
                                  }}
                                />
                                {errors.departureTime && (
                                  <p className="text-xs text-red-500 mt-1">
                                    {errors.departureTime}
                                  </p>
                                )}
                              </div>

                              <div>
                                <SelectFieldset
                                  label={
                                    formData.departureTransportMode === "flight"
                                      ? "Airport"
                                      : "Port"
                                  }
                                  required
                                >
                                  <Select
                                    key={`${formData.departureDate}-${formData.departureTransportMode}`}
                                    value={
                                      formData.departureAirport
                                        ? formData.departureAirport.toString()
                                        : ""
                                    }
                                    onValueChange={(value) => {
                                      const portId = Number(value);
                                      if (!formData.departureDate) {
                                        setFormData((prev) => ({
                                          ...prev,
                                          departureAirport: portId,
                                        }));
                                        return;
                                      }

                                      setFormData((prev) => ({
                                        ...prev,
                                        departureAirport: portId,
                                      }));
                                    }}
                                  >
                                    <SelectTriggerUI className="h-9 text-xs">
                                      <SelectValue placeholder="Select Port" />
                                    </SelectTriggerUI>

                                    <SelectContent>
                                      {getFilteredPorts(
                                        formData.departureTransportMode,
                                        "departure",
                                      ).map((port) => (
                                        <SelectItem
                                          key={port.portId}
                                          value={port.portId.toString()}
                                        >
                                          {port.portName}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </SelectFieldset>
                                {errors.departureAirport && (
                                  <p className="text-xs text-red-500 mt-1">
                                    {errors.departureAirport}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Row 3: Airline and Booking Ref */}
                          {formData.departureTransportMode === "flight" && (
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                              <div>
                                <InputFieldset
                                  label="Airline"
                                  type="text"
                                  placeholder="Enter airline"
                                  className="text-xs"
                                  maxLength={60}
                                  value={formData.departureAirline}
                                  onChange={(e) => {
                                    const value = e.target.value;

                                    if (
                                      !/^[\p{L}\p{M}\p{N}\s-]*$/u.test(value)
                                    ) {
                                      return;
                                    }

                                    setFormData((prev) => ({
                                      ...prev,
                                      departureAirline: value,
                                    }));
                                  }}
                                />
                                {errors.departureAirline && (
                                  <p className="text-xs text-red-500 mt-1">
                                    {errors.departureAirline}
                                  </p>
                                )}
                              </div>
                              <div>
                                <InputFieldset
                                  label="Booking Ref"
                                  placeholder="Booking Ref"
                                  className="text-xs"
                                  maxLength={16}
                                  value={formData.departureBookingRef}
                                  onChange={(e) => {
                                    const value = e.target.value
                                      .toUpperCase()
                                      .replace(/[^A-Z0-9]/g, "");

                                    setFormData((prev) => ({
                                      ...prev,
                                      departureBookingRef: value,
                                    }));
                                  }}
                                  disabled={formData.departureConfirmed}
                                />
                                {errors.departureBookingRef && (
                                  <p className="text-xs text-red-500 mt-1">
                                    {errors.departureBookingRef}
                                  </p>
                                )}
                              </div>
                              <div className="pb-0.5">
                                <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                                  Confirmed
                                </Label>
                                <div className="flex items-center h-9">
                                  <Switch
                                    checked={formData.departureConfirmed}
                                    onCheckedChange={(checked) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        departureConfirmed: checked,
                                      }))
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {transportMode === "road" && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      {/* Arrival Card */}
                      <Card
                        className="border-2 border-primary/40 shadow-sm bg-white"
                        style={{ gap: "0px" }}
                      >
                        <CardHeader className="bg-card" style={{ gap: "0px" }}>
                          <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                              <ArrowRight className="w-5 h-5 text-primary-foreground" />
                            </div>

                            <CardTitle className="text-base font-semibold text-foreground">
                              Arrival Details
                            </CardTitle>
                          </div>
                        </CardHeader>

                        <CardContent className="space-y-4 pt-4 bg-card">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {/* DATE */}
                            <div>
                              <SelectFieldset label="Date" required>
                                <CustomDayPicker
                                    value={formData.arrivalDate}
                                    operatorExpiryDate={
                                      operatorExpiryDate ?? undefined
                                    }
                                    disabledDates={portBlockedDates}
                                    cutoffDates={cutoffBlockedDates}
                                    blockedDateReasons={blockedDateReasons}
                                    month={arrivalCalendarMonth}
                                    onMonthChange={(month) => {
                                      setArrivalCalendarMonth(month);
                                      setDepartureCalendarMonth(month);
                                    }}
                                    disabled={(date) =>
                                      isDateDisabled(date, "arrival")
                                    }
                                    onChange={(date) => {
                                      const formatted = formatLocalDate(date);
                                      setArrivalCalendarMonth(date);
                                      setDepartureCalendarMonth(date);

                                      setFormData((prev) => ({
                                        ...prev,
                                        arrivalDate: formatted,
                                        departureDate:
                                          prev.departureDate &&
                                          new Date(prev.departureDate) <=
                                            new Date(formatted)
                                            ? ""
                                            : prev.departureDate,
                                      }));
                                      setErrors((prev) => ({
                                        ...prev,
                                        arrivalDate: "",
                                      }));
                                    }}
                                  />
                              </SelectFieldset>
                              {errors.arrivalDate && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.arrivalDate}
                                </p>
                              )}
                            </div>

                            {/* TIME */}
                            <div>
                              <InputFieldset
                                label="Time (24hr Format)"
                                required
                                type="text"
                                placeholder="HH:MM"
                                maxLength={5}
                                className="text-xs"
                                value={formData.arrivalTime}
                                onChange={(e) => {
                                  let value = e.target.value.replace(
                                    /[^\d]/g,
                                    "",
                                  );

                                  if (value.length > 4) return;

                                  if (value.length >= 3) {
                                    value = `${value.slice(0, 2)}:${value.slice(2)}`;
                                  }

                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalTime: value,
                                  }));

                                  setErrors((prev) => ({
                                    ...prev,
                                    arrivalTime:
                                      value.length > 0 && !isValidTime(value)
                                        ? "Time must be in 24-hour format (HH:MM)"
                                        : "",
                                  }));
                                }}
                              />
                              {errors.arrivalTime && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.arrivalTime}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* PORT */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <SelectFieldset label="Port" required>
                                <Select
                                  value={
                                    formData.arrivalAirport
                                      ? formData.arrivalAirport.toString()
                                      : ""
                                  }
                                  onValueChange={(value) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      arrivalAirport: Number(value),
                                    }));
                                  }}
                                >
                                  <SelectTriggerUI className="h-9 text-xs">
                                    <SelectValue placeholder="Select Port" />
                                  </SelectTriggerUI>

                                  <SelectContent>
                                    {getFilteredPorts("road", "arrival").map(
                                      (port) => (
                                        <SelectItem
                                          key={port.portId}
                                          value={port.portId.toString()}
                                        >
                                          {port.portName}
                                        </SelectItem>
                                      ),
                                    )}
                                  </SelectContent>
                                </Select>
                              </SelectFieldset>
                              {errors.arrivalAirport && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.arrivalAirport}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-5">
                              <Switch
                                id="arrival-confirmed"
                                checked={formData.arrivalConfirmed}
                                onCheckedChange={(checked) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalConfirmed: checked,
                                  }))
                                }
                              />

                              <Label
                                htmlFor="departure-confirmed"
                                className="text-xs font-medium text-foreground cursor-pointer"
                              >
                                Confirmed
                              </Label>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      {/* Departure Card */}
                      <Card
                        className="border-2 border-orange-400/40 shadow-sm bg-white"
                        style={{ gap: "0px" }}
                      >
                        <CardHeader className="bg-card" style={{ gap: "0px" }}>
                          <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-full bg-orange-600 flex items-center justify-center">
                              <ArrowRight className="w-5 h-5 text-destructive-foreground" />
                            </div>

                            <CardTitle className="text-base font-semibold text-foreground">
                              Departure Details
                            </CardTitle>
                          </div>
                        </CardHeader>

                        <CardContent className="space-y-4 pt-4 bg-card">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {/* DATE */}
                            <div>
                              <SelectFieldset label="Date" required>
                                <CustomDayPicker
                                  value={formData.departureDate}
                                  operatorExpiryDate={
                                    operatorExpiryDate ?? undefined
                                  }
                                  disabledDates={portBlockedDates}
                                  blockedDateReasons={blockedDateReasons}
                                  cutoffDates={cutoffBlockedDates}
                                  minDate={
                                    formData.arrivalDate
                                      ? new Date(formData.arrivalDate)
                                      : undefined
                                  }
                                  month={departureCalendarMonth}
                                  onMonthChange={(month) => {
                                    setDepartureCalendarMonth(month);
                                  }}
                                  disabled={(date) =>
                                    isDateDisabled(date, "departure")
                                  }
                                  onChange={(date) => {
                                    const formatted = formatLocalDate(date);

                                    setFormData((prev) => ({
                                      ...prev,
                                      departureDate: formatted,
                                    }));
                                  }}
                                />
                              </SelectFieldset>
                              {errors.departureDate && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.departureDate}
                                </p>
                              )}
                            </div>

                            {/* TIME */}
                            <div>
                              <InputFieldset
                                label="Time (24hr Format)"
                                required
                                type="text"
                                placeholder="HH:MM"
                                maxLength={5}
                                className="text-xs"
                                value={formData.departureTime}
                                onChange={(e) => {
                                  let value = e.target.value.replace(
                                    /[^\d]/g,
                                    "",
                                  );

                                  if (value.length > 4) return;

                                  if (value.length >= 3) {
                                    value = `${value.slice(0, 2)}:${value.slice(2)}`;
                                  }

                                  setFormData((prev) => ({
                                    ...prev,
                                    departureTime: value,
                                  }));

                                  setErrors((prev) => ({
                                    ...prev,
                                    departureTime:
                                      value.length > 0 && !isValidTime(value)
                                        ? "Time must be in 24-hour format (HH:MM)"
                                        : "",
                                  }));
                                }}
                              />
                              {errors.departureTime && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.departureTime}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* PORT */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <SelectFieldset label="Port" required>
                                <Select
                                  value={
                                    formData.departureAirport
                                      ? formData.departureAirport.toString()
                                      : ""
                                  }
                                  onValueChange={(value) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      departureAirport: Number(value),
                                    }));
                                  }}
                                >
                                  <SelectTriggerUI className="h-9 text-xs">
                                    <SelectValue placeholder="Select Port" />
                                  </SelectTriggerUI>

                                  <SelectContent>
                                    {getFilteredPorts("road", "departure").map(
                                      (port) => (
                                        <SelectItem
                                          key={port.portId}
                                          value={port.portId.toString()}
                                        >
                                          {port.portName}
                                        </SelectItem>
                                      ),
                                    )}
                                  </SelectContent>
                                </Select>
                              </SelectFieldset>
                              {errors.departureAirport && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.departureAirport}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-5">
                              <Switch
                                id="departure-confirmed"
                                checked={formData.departureConfirmed}
                                onCheckedChange={(checked) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    departureConfirmed: checked,
                                  }))
                                }
                              />

                              <Label
                                htmlFor="departure-confirmed"
                                className="text-xs font-medium text-foreground cursor-pointer"
                              >
                                Confirmed
                              </Label>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              )}

              {/* Tab 2: Venue Summary */}
              {currentStep === 2 && (
                <div className="space-y-5">
                  <Card
                    className="bg-white border border-border shadow-sm"
                    style={{ gap: "0px" }}
                  >
                    <CardHeader className="pb-4">
                      <CardTitle className="text-lg font-semibold">
                        Venue Summary
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground">
                        Configure tour details, accommodation and venues
                      </CardDescription>
                    </CardHeader>

                    <CardContent style={{ padding: "0px 24px 20px 24px" }}>
                      <div
                        className="grid grid-cols-1 md:grid-cols-12 gap-3 border rounded-lg p-4 bg-muted/30 hover:shadow-sm transition"
                        style={{ paddingLeft: "16px", paddingRight: "16px" }}
                      >
                        {/* Tour Name */}
                        <div className="md:col-span-3 space-y-2">
                          <InputFieldset
                            label="Tour Name"
                            required
                            placeholder="Enter tour name"
                            className="text-sm"
                            maxLength={50}
                            value={formData.tourName}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (value.length > 50) return;

                              if (!/^[A-Za-z0-9 ]*$/.test(value)) return;

                              setFormData((prev) => ({
                                ...prev,
                                tourName: value,
                              }));
                            }}
                          />
                          {errors.tourName && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.tourName}
                            </p>
                          )}
                        </div>

                        {/* Applicants */}
                        <div className="md:col-span-3 space-y-2">
                          <InputFieldset
                            label="No. of Applicants"
                            required
                            type="number"
                            placeholder="0"
                            min={0}
                            max={200}
                            className="text-sm"
                            value={formData.noOfApplicants}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (value === "") {
                                setFormData((prev) => ({
                                  ...prev,
                                  noOfApplicants: "",
                                }));
                                return;
                              }

                              const num = Number(value);

                              if (num < 0 || num > 200) return;

                              setFormData((prev) => ({
                                ...prev,
                                noOfApplicants: value,
                              }));
                            }}
                          />
                          {errors.noOfApplicants && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.noOfApplicants}
                            </p>
                          )}
                        </div>

                        {/* Parent Tour Id */}
                        <div className="md:col-span-3 space-y-2">
                          <InputFieldset
                            label="Parent Tour Id"
                            placeholder="Enter Parent Tour Id"
                            className="text-sm"
                            maxLength={50}
                            value={formData.parentTourId}
                            onChange={(e) => {
                              const value = e.target.value.toUpperCase();

                              // Allow only letters, numbers and hyphen
                              if (!/^[A-Z0-9-]*$/.test(value)) {
                                return;
                              }

                              if (value.length > 50) {
                                return;
                              }

                              setFormData((prev) => ({
                                ...prev,
                                parentTourId: value,
                              }));

                              // Clear error while user is correcting the value
                              setErrors((prev) => ({
                                ...prev,
                                parentTourId: "",
                              }));
                            }}
                          />

                          {errors.parentTourId && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.parentTourId}
                            </p>
                          )}
                        </div>

                        {/* Faiz */}
                        {/* <div className="md:col-span-3 space-y-2">
                          <Label className="text-sm font-medium text-muted-foreground">
                            Faiz Accommodation{" "}
                            <span className="text-red-500">*</span>
                          </Label>

                          <RadioGroup
                            value={formData.faizAccommodation}
                            className="flex gap-6 items-center h-10 pointer-events-none opacity-80"
                          >
                            <div className="flex items-center gap-2">
                              <RadioGroupItem
                                value="yes"
                                id="faiz-yes"
                                className="
                                  h-5 w-5 rounded-full
                                  border-2 border-gray-200
                                  flex items-center justify-center
                                  data-[state=checked]:border-primary
                                "
                              >
                                <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                              </RadioGroupItem>

                              <Label
                                htmlFor="faiz-yes"
                                className="text-sm cursor-pointer"
                              >
                                Yes
                              </Label>
                            </div>

                            <div className="flex items-center gap-2">
                              <RadioGroupItem
                                value="no"
                                id="faiz-no"
                                className="
                                  h-5 w-5 rounded-full
                                  border-2 border-gray-200
                                  flex items-center justify-center
                                  data-[state=checked]:border-primary
                                "
                              >
                                <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                              </RadioGroupItem>

                              <Label
                                htmlFor="faiz-no"
                                className="text-sm cursor-pointer"
                              >
                                No
                              </Label>
                            </div>
                          </RadioGroup>
                        </div> */}
                      </div>

                      {/* ================= VENUE SECTION ================= */}
                      <div className="space-y-4" style={{ marginTop: "14px" }}>
                        {/* Section Header */}
                        <div
                          className="flex"
                          style={{
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <div>
                            <h3 className="text-sm font-semibold text-foreground">
                              Venue Schedule{" "}
                              <span className="text-red-500">*</span>
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              Add venues with check-in and check-out details
                            </p>
                          </div>
                          {/* Add Button */}
                          <div
                            className="flex justify-end"
                            style={{ marginTop: "20px" }}
                          >
                            {canAddVenue() && (
                              <Button
                                type="button"
                                className="h-10 px-4 text-sm font-medium"
                                onClick={() => {
                                  const newVenue = {
                                    id: `venue-${Date.now()}`,
                                    venueId: null,
                                    fromDate: "",
                                    fromTime: "",
                                    toDate: "",
                                    toTime: "",
                                  };

                                  setFormData((prev) => {
                                    const updated = [...prev.venues];
                                    updated.splice(1, 0, newVenue);

                                    return {
                                      ...prev,
                                      venues: updated,
                                    };
                                  });
                                }}
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Venue
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Empty State */}
                        {formData.venues.length === 0 ? (
                          <div className="border border-dashed border-border rounded-lg p-10 text-center">
                            <MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                            <p className="text-sm text-muted-foreground">
                              No venues added yet
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {formData.venues.map((venue, index) => {
                              const isFirst = index === 0;
                              const isLast =
                                index === formData.venues.length - 1;
                              return (
                                <div
                                  key={venue.id}
                                  className="border rounded-lg p-4 bg-muted/30 hover:shadow-sm transition"
                                >
                                  <div className="flex gap-4">
                                    {/* <div className="w-7 h-7 flex items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                                    {index + 1}
                                  </div> */}
                                    <div className="flex-1 space-y-4">
                                      <div>
                                        {/* Venue Name */}
                                        <div
                                          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
                                          style={{ alignItems: "center" }}
                                        >
                                          <div className="space-y-1">
                                            <SelectFieldset
                                              label={`Venue Name ${index + 1}`}
                                              required
                                              error={
                                                errors[`venueName-${index}`]
                                              }
                                            >
                                              <Select
                                                value={
                                                  venue.venueId
                                                    ? venue.venueId.toString()
                                                    : ""
                                                }
                                                onValueChange={(value) => {
                                                  const updated = [
                                                    ...formData.venues,
                                                  ];
                                                  updated[index].venueId =
                                                    Number(value);
                                                  setIsVenueManuallyChanged(
                                                    false,
                                                  );
                                                  setFormData((prev) => ({
                                                    ...prev,
                                                    venues: updated,
                                                  }));
                                                }}
                                              >
                                                <SelectTriggerUI className="h-10 text-sm">
                                                  <SelectValue placeholder="Select venue">
                                                    {venue.venueId
                                                      ? venuesFromApi.find(
                                                          (v) =>
                                                            v.venueId ===
                                                            venue.venueId,
                                                        )?.venueName
                                                      : "Select venue"}
                                                  </SelectValue>
                                                </SelectTriggerUI>

                                                <SelectContent>
                                                  {venuesFromApi.map((v) => (
                                                    <SelectItem
                                                      key={v.venueId}
                                                      value={v.venueId.toString()}
                                                    >
                                                      {v.venueName}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </SelectFieldset>
                                          </div>
                                          <div className="space-y-1"></div>
                                          <div className="space-y-1"></div>
                                          <div
                                            className="space-y-1"
                                            style={{
                                              display: "flex",
                                              justifyContent: "end",
                                            }}
                                          >
                                            {/* Delete */}
                                            {!(isFirst || isLast) && (
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                                onClick={() => {
                                                  const updated =
                                                    formData.venues.filter(
                                                      (_, i) => i !== index,
                                                    );

                                                  setIsVenueManuallyChanged(
                                                    false,
                                                  ); // allow auto recalculation

                                                  setFormData((prev) => ({
                                                    ...prev,
                                                    venues: updated,
                                                  }));
                                                }}
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      {/* Dates */}
                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                        {/* Check-In Date */}
                                        <div className="space-y-1">
                                          <SelectFieldset
                                            label="Check-In Date"
                                            required
                                            error={errors[`fromDate-${index}`]}
                                          >
                                            <CustomDayPicker
                                              value={venue.fromDate}
                                              operatorExpiryDate={
                                                operatorExpiryDate ?? undefined
                                              }
                                              mode="range"
                                              month={
                                                venueCalendarMonths[
                                                  `checkin-${index}`
                                                ] ??
                                                (venue.fromDate
                                                  ? new Date(venue.fromDate)
                                                  : undefined)
                                              }
                                              onMonthChange={(month) => {
                                                setVenueCalendarMonths(
                                                  (prev) => ({
                                                    ...prev,
                                                    [`checkin-${index}`]: month,
                                                  }),
                                                );
                                              }}
                                              disabledDates={portBlockedDates}
                                              cutoffDates={cutoffBlockedDates}
                                              highlightRange={{
                                                from: formData.arrivalDate,
                                                to: formData.departureDate,
                                              }}
                                              disabled={(date) =>
                                                isFirst ||
                                                isDateDisabled(
                                                  date,
                                                  "arrival",
                                                ) ||
                                                isOutsideTourRange(date)
                                              }
                                              onChange={(date: Date) => {
                                                if (isFirst) return;

                                                const formatted =
                                                  formatLocalDate(date);

                                                const updated = [
                                                  ...formData.venues,
                                                ];
                                                updated[index].fromDate =
                                                  formatted;

                                                setFormData((prev) => ({
                                                  ...prev,
                                                  venues: updated,
                                                }));
                                              }}
                                              placeholder="dd-MM-yyyy"
                                            />
                                          </SelectFieldset>
                                        </div>

                                        {/* Check-In Time */}
                                        <div className="space-y-1">
                                          <div className="relative group">
                                            <InputFieldset
                                              label="Check-In Time"
                                              required
                                              type="time"
                                              className={`text-sm pr-8 border border-gray-300 rounded-md transition-all outline-none ${
                                                isFirst
                                                  ? "text-gray-600 border-gray-200 cursor-not-allowed opacity-70"
                                                  : ""
                                              }`}
                                              value={venue.fromTime}
                                              disabled
                                              onChange={(e) => {
                                                if (isFirst) return;

                                                const updated = [
                                                  ...formData.venues,
                                                ];
                                                updated[index].fromTime =
                                                  e.target.value;

                                                setFormData((prev) => ({
                                                  ...prev,
                                                  venues: updated,
                                                }));
                                              }}
                                            />
                                            <CalculationBox
                                              type="checkin"
                                              index={index}
                                              isLast={
                                                index ===
                                                formData.venues.length - 1
                                              }
                                              data={getCalculationBreakdown(
                                                venue,
                                                index,
                                                "checkin",
                                              )}
                                            />
                                          </div>
                                          {errors[`fromTime-${index}`] && (
                                            <p className="text-xs text-red-500 mt-1">
                                              {errors[`fromTime-${index}`]}
                                            </p>
                                          )}
                                        </div>

                                        {/* Check-Out Date */}
                                        <div className="space-y-1">
                                          <SelectFieldset
                                            label="Check-Out Date"
                                            required
                                            error={errors[`toDate-${index}`]}
                                          >
                                            <CustomDayPicker
                                              value={venue.toDate}
                                              operatorExpiryDate={
                                                operatorExpiryDate ?? undefined
                                              }
                                              mode="range"
                                              month={
                                                venueCalendarMonths[
                                                  `checkout-${index}`
                                                ] ??
                                                (venue.toDate
                                                  ? new Date(venue.toDate)
                                                  : undefined)
                                              }
                                              onMonthChange={(month) => {
                                                setVenueCalendarMonths(
                                                  (prev) => ({
                                                    ...prev,
                                                    [`checkout-${index}`]:
                                                      month,
                                                  }),
                                                );
                                              }}
                                              highlightRange={{
                                                from: formData.arrivalDate,
                                                to: formData.departureDate,
                                              }}
                                              disabledDates={portBlockedDates}
                                              cutoffDates={cutoffBlockedDates}
                                              disabled={(date) =>
                                                isLast ||
                                                isDateDisabled(
                                                  date,
                                                  "departure",
                                                ) ||
                                                isOutsideTourRange(date)
                                              }
                                              onChange={(date: Date) => {
                                                const formatted =
                                                  formatLocalDate(date);

                                                setIsVenueManuallyChanged(true);

                                                setFormData((prev) => {
                                                  const updated = [
                                                    ...prev.venues,
                                                  ];
                                                  updated[index].toDate =
                                                    formatted;
                                                  if (
                                                    index <
                                                    updated.length - 1
                                                  ) {
                                                    updated[
                                                      index + 1
                                                    ].fromDate = formatted;
                                                  }

                                                  return {
                                                    ...prev,
                                                    venues: updated,
                                                  };
                                                });
                                              }}
                                              placeholder="dd-MM-yyyy"
                                            />
                                          </SelectFieldset>
                                        </div>

                                        {/* Check-Out Time */}
                                        <div className="space-y-1">
                                          <div className="relative group">
                                            <InputFieldset
                                              label="Check-Out Time"
                                              required
                                              type="time"
                                              className={`text-sm border border-gray-300 rounded-md transition-all outline-none ${
                                                isLast
                                                  ? "text-gray-600 border-gray-200 cursor-not-allowed opacity-70"
                                                  : ""
                                              }`}
                                              value={venue.toTime}
                                              disabled
                                              onChange={(e) => {
                                                if (isLast) return;

                                                setIsVenueManuallyChanged(true);

                                                setFormData((prev) => {
                                                  const updated = [
                                                    ...prev.venues,
                                                  ];

                                                  updated[index].toTime =
                                                    e.target.value;
                                                  if (
                                                    index <
                                                    updated.length - 1
                                                  ) {
                                                    updated[
                                                      index + 1
                                                    ].fromTime = e.target.value;
                                                  }

                                                  return {
                                                    ...prev,
                                                    venues: updated,
                                                  };
                                                });
                                              }}
                                            />
                                            <CalculationBox
                                              type="checkout"
                                              index={index}
                                              isLast={
                                                index ===
                                                formData.venues.length - 1
                                              }
                                              data={getCalculationBreakdown(
                                                venue,
                                                index,
                                                "checkout",
                                              )}
                                            />
                                          </div>
                                          {errors[`toTime-${index}`] && (
                                            <p className="text-xs text-red-500 mt-1">
                                              {errors[`toTime-${index}`]}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Tab 3: Rooms */}
              {currentStep === 3 && (
                <AvailableRooms
                  formData={formData}
                  roomAvailability={roomAvailability}
                  expandedProperties={expandedProperties}
                  errors={errors}
                  availabilityData={availabilityData}
                  getVenueName={getVenueName}
                  getDatesInRange={getDatesInRange}
                  handleRoomRequestUpdate={handleRoomRequestUpdate}
                  togglePropertyExpand={togglePropertyExpand}
                  getTotalRequestedCapacity={getTotalRequestedCapacity}
                />
              )}

              {/* Tab 4: Confirmation */}
              {currentStep === 4 && (
                <Confirmation
                  formData={formData}
                  roomAvailability={roomAvailability}
                  ports={ports}
                  properties={properties}
                  termsAndConditions={formData.termsAndConditions}
                  lawazimConfigs={lawazimConfigs}
                  expandedRoomDetails={expandedRoomDetails}
                  setExpandedRoomDetails={setExpandedRoomDetails}
                  getTotalRequestedCapacity={getTotalRequestedCapacity}
                  getVenueName={getVenueName}
                  handleFinalConfirmation={handleFinalConfirmation}
                  setCurrentStep={setCurrentStep}
                />
              )}
            </div>

            {/* Navigation Buttons */}
            {currentStep !== 4 && (
              <div
                className="flex justify-between"
                style={{ marginTop: "20px" }}
              >
                {currentStep > 1 ? (
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    style={{ background: "#5B9BD5" }}
                    className="text-white text-xs h-9"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Previous
                  </Button>
                ) : (
                  <div />
                )}

                <Button
                  onClick={handleNext}
                  disabled={
                    currentStep === 3 && (!!errors.capacity || !!errors.rooms)
                  }
                  style={{ background: "#5B9BD5" }}
                  className="text-white"
                >
                  Next
                  <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
